import {
  AsyncCache,
  CookieNames,
  fromEnv,
  getCookie,
  setHeraCookie,
} from "src/helpers";
import axios from "axios";
import {
  IBankValidationPayload,
  IBankValidationResult,
  IMerchantJSON,
  IActivationJSON,
  IActivationKYB,
} from "src/interfaces";

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    return new Promise((resolve, reject) => {
      const originalRequest = error.config;

      const requiresTokenRefresh =
        error?.response?.status === 401 &&
        (error?.response?.data?.errors?.[0]?.code === "HTTP_401" ||
          error?.response?.data?.message === "invalid auth header") &&
        !error?.config?.__isRetryRequest;

      if (requiresTokenRefresh) {
        originalRequest.__isRetryRequest = true;

        const refreshUrl = fromEnv("REACT_APP_MARS_HOST") + "/gobiz/goid/token";
        const refreshToken = getCookie(CookieNames.HeraRefreshToken);
        const token = getCookie(CookieNames.HeraToken);

        if (!refreshToken) {
          setHeraCookie(CookieNames.CurrentPath, fromEnv("REACT_APP_HOST"));
          return (window.location.href =
            fromEnv("REACT_APP_HERA_HOST") + "/login");
        }

        const payload: any = {
          client_id: fromEnv("REACT_APP_GOID_CLIENT_ID"),
          grant_type: "refresh_token",
          data: {
            refresh_token: refreshToken,
          },
        };

        const response = axios
          .post(refreshUrl, payload, {
            headers: {
              "content-type": "application/json",
              Authorization: "Bearer " + token,
              "Authentication-Type": "go-id",
            },
          })
          .then((response) => {
            const { data } = response;

            setHeraCookie(CookieNames.HeraRefreshToken, data.refresh_token);
            setHeraCookie(CookieNames.HeraToken, data.access_token);

            const token = getCookie(CookieNames.HeraToken);
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch(() => {
            setHeraCookie(CookieNames.CurrentPath, fromEnv("REACT_APP_HOST"));
            return (window.location.href =
              fromEnv("REACT_APP_HERA_HOST") + "/login");
          });

        resolve(response);
      }

      reject(error);
    });
  }
);

export class PassportService extends AsyncCache {
  token = getCookie(CookieNames.HeraToken) || "";

  getCurrentUser() {
    return axios.get(`${fromEnv<string>("REACT_APP_MARS_HOST")}/v1/users/me`, {
      headers: {
        Authorization: "Bearer " + this.token,
        "Authentication-Type": "go-id",
      },
    });
  }

  getUserOutlet(outletId: string) {
    return axios.get(
      `${fromEnv<string>("REACT_APP_MARS_HOST")}/v1/merchants/${outletId}`,
      {
        headers: {
          Authorization: "Bearer " + this.token,
          "Authentication-Type": "go-id",
        },
      }
    );
  }

  updateOutlet(outletId: string, data: Partial<IMerchantJSON>) {
    return axios.patch(
      `${fromEnv<string>("REACT_APP_MARS_HOST")}/v1/merchants/${outletId}`,
      data,
      {
        headers: {
          Authorization: "Bearer " + this.token,
          "Authentication-Type": "go-id",
        },
      }
    );
  }

  validateBankAccount(data: IBankValidationPayload) {
    return axios.post<IBankValidationResult>(
      `${fromEnv("REACT_APP_MARS_HOST")}/v1/validations/bank_account_name`,
      data,
      {
        headers: {
          Authorization: "Bearer " + this.token,
          "Authentication-Type": "go-id",
        },
      }
    );
  }

  postBankAccount(outletId: string, data: Partial<IMerchantJSON>) {
    return axios.patch<IMerchantJSON>(
      `${fromEnv<string>("REACT_APP_MARS_HOST")}/v1/merchants/${outletId}`,
      data,
      {
        headers: {
          Authorization: "Bearer " + this.token,
          "Authentication-Type": "go-id",
        },
      }
    );
  }

  postConsent(version: string) {
    return axios.post(
      `${fromEnv<string>(
        "REACT_APP_OPD_HOST"
      )}/api/merchant/onboarding/consents`,
      { privacy_policy_version: version },
      {
        headers: {
          Authorization: "Bearer " + this.token,
          "Authentication-Type": "go-id",
        },
      }
    );
  }

  getPassportActivation() {
    return axios.get(
      `${fromEnv<string>("REACT_APP_OPD_HOST")}/api/passport/onboarding`,
      {
        headers: {
          Authorization: "Bearer " + this.token,
          "Authentication-Type": "go-id",
        },
      }
    );
  }

  updatePassportActivation(data: Partial<IActivationJSON>) {
    return axios.post(
      `${fromEnv<string>("REACT_APP_OPD_HOST")}/api/passport/onboarding`,
      data,
      {
        headers: {
          Authorization: "Bearer " + this.token,
          "Authentication-Type": "go-id",
        },
      }
    );
  }

  doPassportKYB(data: IActivationKYB) {
    return axios.post(
      `${fromEnv<string>("REACT_APP_OPD_HOST")}/api/passport/onboarding/kyb`,
      data,
      {
        headers: {
          Authorization: "Bearer " + this.token,
          "Authentication-Type": "go-id",
        },
      }
    );
  }

  generateUrl(userId: number, key: string) {
    return axios.post(
      fromEnv<string>("REACT_APP_MARS_HOST") + `/v1/users/${userId}/upload_url`,
      {
        key,
      },
      {
        headers: {
          Authorization: "Bearer " + this.token,
          "Authentication-Type": "go-id",
        },
      }
    );
  }

  uploadFile(patchUrl: string, file: File) {
    return axios.put(patchUrl, file);
  }
}
